import { School } from '../types/School';
import { User } from '../types/User'; // Assuming User type exists
import { getSchoolPictureUploadUrl } from './aws';
import axiosInstance from './axiosCreator';
import axios from 'axios';

export const fetchSchools = async (): Promise<School[] | undefined> => {
  try {
    const response = await axiosInstance.get('/schools');
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch schools');
    }
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch schools:', error);
    return undefined;
  }
};

export const addSchool = async (schoolName: string, userId: string) => {
  try {
    const response = await axiosInstance.post('/schools/create', {
      name: schoolName,
      userId,
    });
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to add school');
    }
    return response.data.data;
  } catch (error) {
    console.error('Failed to add school:', error);
    return undefined;
  }
};

export const getStudentsBySchool = async (
  schoolId: string
): Promise<User[]> => {
  try {
    const response = await axiosInstance.get(`/schools/${schoolId}/students`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch students');
    }
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch students:', error);

    return [];
  }
};

export const removeUserFromSchool = async (
  schoolId: string,
  userId: string
): Promise<User | undefined> => {
  try {
    const response = await axiosInstance.delete(
      `/schools/${schoolId}/students/${userId}`
    );
    if (!response.data.success) {
      throw new Error(
        response.data.error || 'Failed to remove user from school'
      );
    }
    return response.data.data; // Backend returns updated user
  } catch (error) {
    console.error('Failed to remove user from school:', error);
    return undefined;
  }
};

export const updateSchoolDomains = async (
  schoolId: string,
  domains: string[]
): Promise<School | undefined> => {
  try {
    const response = await axiosInstance.post(`/schools/${schoolId}/domains`, {
      domains,
    });
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update school domains');
    }
    return response.data.data;
  } catch (error) {
    console.error('Failed to update school domains:', error);
    return undefined;
  }
};

export const updateSchoolShortName = async (
  schoolId: string,
  shortName: string
): Promise<School | undefined> => {
  try {
    const response = await axiosInstance.post(
      `/schools/${schoolId}/shortName`,
      {
        shortName,
      }
    );
    if (!response.data.success) {
      throw new Error(
        response.data.error || 'Failed to update school short name'
      );
    }
    return response.data.data;
  } catch (error) {
    console.error('Failed to update school short name:', error);
    return undefined;
  }
};

export const updateSchoolImageBE = async (
  schoolId: string,
  image: string
): Promise<School | undefined> => {
  try {
    console.log('Updating school image:', schoolId, image);
    const response = await axiosInstance.post(`/schools/${schoolId}/image`, {
      image,
    });
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update school image');
    }
    return response.data.data;
  } catch (error) {
    console.error('Failed to update school image:', error);
    return undefined;
  }
};

export const uploadSchoolPictureToS3 = async (file: File): Promise<string> => {
  if (!file) return '';

  const bucket = 'ora-imgs';
  const dirName = `schools/${file.name}`;
  const data = await getSchoolPictureUploadUrl(bucket, dirName, file.name);

  if (data.url) {
    try {
      await axios.put(data.url, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      return `https://ora-imgs.s3.us-west-1.amazonaws.com/${dirName}/${file.name}`;
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error(
        `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  throw new Error('Failed to get presigned URL');
};

export const fetchCohortFromReferralId = async (
  referralId: string
): Promise<string> => {
  const response = await axiosInstance.get(
    `/public/cohort-from-refId/${referralId}`
  );
  return response.data.data;
};

export const fetchCohortImage = async (cohort: string): Promise<string> => {
  const response = await axiosInstance.get(`/public/cohort-image/${cohort}`);
  return response.data.data;
};

export const verifySchool = async (
  schoolId: string
): Promise<School | undefined> => {
  try {
    const response = await axiosInstance.post(`/schools/${schoolId}/verify`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to verify school');
    }
    return response.data.data;
  } catch (error) {
    console.error('Failed to verify school:', error);
    return undefined;
  }
};

export const userRequestExamsForSchool = async (schoolId: string) => {
  const response = await axiosInstance.post(
    `/schools/${schoolId}/requestExams`
  );
  return response.data.data;
};

export const fetchSchoolDomains = async (): Promise<string[]> => {
  const response = await axiosInstance.get('/public/school-domains');
  return response.data.data;
};
